/**
 * Bulk search UI state — the search inputs shared by the one bulk search
 * component across all three entities (payment / artifact / execution).
 *
 * The id list and page size are shared across regions; the page number and the
 * refine-filter tokens are PER REGION, because each region renders an
 * independent table that paginates and filters on its own. Results live in the
 * per-entity search slices. This slice is entity-guarded: `entity` records which
 * entity the current state belongs to, so navigating from one bulk page to
 * another re-seeds cleanly (resetBulkSearch). The `?ids=` query param mirrors
 * `ids` for shareable, refresh-proof links.
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { PropertyFilterProps } from '@cloudscape-design/components/property-filter';

/** PropertyFilter tokens, stored mutable so Immer/TypeScript stay happy. */
export type FilterTokens = PropertyFilterProps.Query['tokens'][number][];

export const DEFAULT_PAGE_SIZE = 50;

/** One region's independent query controls. */
export interface RegionQueryUi {
  /** 1-based page index (matches the search API and Cloudscape Pagination). */
  pageNumber: number;
  /** Status/state refine-filter tokens for this region's table. */
  filterTokens: FilterTokens;
}

export interface BulkSearchUiState {
  /** Entity the current query belongs to ('payment' | 'artifact' | 'execution'); '' before first use. */
  entity: string;
  /** Committed, de-duplicated id list driving the search (shared across regions). */
  ids: string[];
  /** Shared page size for every region's table. */
  pageSize: number;
  /** Per-region page + filter, keyed by region id. */
  byRegion: Record<string, RegionQueryUi>;
}

export const bulkSearchUiInitialState: BulkSearchUiState = {
  entity: '',
  ids: [],
  pageSize: DEFAULT_PAGE_SIZE,
  byRegion: {},
};

const freshRegion = (): RegionQueryUi => ({ pageNumber: 1, filterTokens: [] });

const bulkSearchUiSlice = createSlice({
  name: 'bulkSearchUi',
  initialState: bulkSearchUiInitialState,
  reducers: {
    /** Start a new search for an entity: set the ids, clear every region's page + filter. Page size sticks. */
    resetBulkSearch: (state, action: PayloadAction<{ entity: string; ids: string[] }>) => {
      state.entity = action.payload.entity;
      state.ids = action.payload.ids;
      state.byRegion = {};
    },
    /** Change the shared page size; every region returns to page 1 (its filter is kept). */
    setBulkPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      Object.values(state.byRegion).forEach((r) => { r.pageNumber = 1; });
    },
    /** Set one region's current page. */
    setRegionPage: (state, action: PayloadAction<{ region: string; page: number }>) => {
      const r = state.byRegion[action.payload.region] || freshRegion();
      r.pageNumber = action.payload.page;
      state.byRegion[action.payload.region] = r;
    },
    /** Set one region's refine-filter tokens; that region returns to page 1. */
    setRegionFilterTokens: (state, action: PayloadAction<{ region: string; tokens: FilterTokens }>) => {
      state.byRegion[action.payload.region] = { pageNumber: 1, filterTokens: action.payload.tokens };
    },
  },
});

export const {
  resetBulkSearch, setBulkPageSize, setRegionPage, setRegionFilterTokens,
} = bulkSearchUiSlice.actions;
export default bulkSearchUiSlice.reducer;
